import {
  ContactConfig,
  ContactFieldsConfig,
  ContactPreferencesConfig,
  ContactPreferenceType,
} from "../../../../../types/ContactConfig";
import { ContactDetails } from "../../../../../types/ContactDetailsValues";
import { AssignedRoles, RoleLabel, } from "../../../../../types/ContactDetailRoles";

/**
 * Sets relevant field in AssignedRoles argument based on RoleLabel.
 */
export const roleLabelToValueMap: Record<
  RoleLabel,
  (roles: AssignedRoles) => boolean
> = {
  Requester: (roles) => roles.requester,
  "Property owner": (roles) => roles.propertyOwner,
  "Quote payer": (roles) => roles.quotePayer,
  "Site contact": (roles) => roles.siteAccessContact,
};

/**
 * Returns a partial AssignedRoles with value set to relevant field based on RoleLabel.
 */
export const buildAssignedRoles: Record<
  RoleLabel,
  (value: boolean) => Partial<AssignedRoles>
> = {
  Requester: (value) => ({ requester: value }),
  "Property owner": (value) => ({ propertyOwner: value }),
  "Quote payer": (value) => ({ quotePayer: value }),
  "Site contact": (value) => ({ siteAccessContact: value }),
};

/**
 * Returns true if some role is assigned.
 */
export const hasAnyRoleAssigned = (roles: AssignedRoles): boolean => {
  return Object.values(roles).some(Boolean);
};

/**
 * Returns true if the expected role is assigned and only the expected role.
 */
export const hasOnlyTargetRoleAssigned = (
  role: RoleLabel,
  roles: AssignedRoles,
): boolean => {
  const isRoleAssigned = roleLabelToValueMap[role](roles);
  const count = Object.values(roles).filter((bool) => bool).length;
  return isRoleAssigned && count === 1;
};

/**
 * Merges two partial AssignedRoles to return a whole AssignedRoles.
 */
export const mergeAssignedRoles = (
  roles1: Partial<AssignedRoles>,
  roles2: Partial<AssignedRoles>,
): AssignedRoles => {
  return {
    requester: roles1.requester || roles2.requester || false,
    propertyOwner: roles1.propertyOwner || roles2.propertyOwner || false,
    quotePayer: roles1.quotePayer || roles2.quotePayer || false,
    siteAccessContact:
      roles1.siteAccessContact || roles2.siteAccessContact || false,
  };
};

/**
 * Replaces values in role1 with the partial role2 values if they exist.
 */
export const replaceAssignedRoles = (
  roles1: AssignedRoles,
  roles2: Partial<AssignedRoles>,
): AssignedRoles => {
  return {
    requester:
      roles2.requester !== undefined ? roles2.requester : roles1.requester,
    propertyOwner:
      roles2.propertyOwner !== undefined
        ? roles2.propertyOwner
        : roles1.propertyOwner,
    quotePayer:
      roles2.quotePayer !== undefined ? roles2.quotePayer : roles1.quotePayer,
    siteAccessContact:
      roles2.siteAccessContact !== undefined
        ? roles2.siteAccessContact
        : roles1.siteAccessContact,
  };
};

/**
 * Creates a display header for this contact with all the contacts roles.
 * primaryRoleLabel will always be at the beginning.
 * E.g. "Requester / Property owner / Site contact"
 */
export const mapAssignedRolesAsHeader = (
  primaryRoleLabel: RoleLabel,
  roles: AssignedRoles,
): string => {
  const roleSet = new Set<string>();
  roleSet.add(primaryRoleLabel);
  if (roles.requester) {
    roleSet.add("Requester");
  }
  if (roles.propertyOwner) {
    roleSet.add("Property owner");
  }
  if (roles.quotePayer) {
    roleSet.add("Quote payer");
  }
  if (roles.siteAccessContact) {
    roleSet.add("Site contact");
  }
  return Array.from(roleSet).join(" / ");
};

/**
 * Returns true if details need to be captured for this contact
 * i.e. if they selected "New contact" radio button.
 */
export const shouldDisplayNewContactDetailsForm = (
  contactConfig: ContactConfig,
  contact: ContactDetails,
) => {
  const targetRole = contactConfig.role;
  const contactAssignedRoles = contact.roles;
  return roleLabelToValueMap[targetRole](contactAssignedRoles);
};

/**
 * Returns true if contact is in an assignable state. I.e. can other contacts assign their role to this contact.
 * Assignable state is when the contacts sameAsRole is also their role. (they selected the "New contact" button),
 * or undefined (nothing selected yet).
 */
export const isContactAssignable = (contact: ContactDetails) => {
  return (
    contact.sameAsRole === undefined || contact.sameAsRole === contact.role
  );
};

/**
 * Returns true if given role has been assigned to any contact besides their self.
 */
export const hasRoleBeenAssignedToContact = (
  role: RoleLabel,
  contactDetails: ContactDetails[],
): boolean =>
  contactDetails.some(
    (contact) => contact.sameAsRole === role && contact.role !== role,
  );

/**
 *  Assign role to delegate contact.
 */
export const assignSelectedRoleToOtherContact = (
  thisContactRole: RoleLabel,
  sameAsRole: RoleLabel,
  contactDetails: ContactDetails[],
  setAssignedRolesFieldValue: (index: number, roles: AssignedRoles) => void,
): void => {
  contactDetails.forEach((delegateContact, i) => {
    if (delegateContact.role !== sameAsRole) {
      return;
    }
    const newRoles = buildAssignedRoles[thisContactRole](true);
    const mergedRoles = mergeAssignedRoles(delegateContact.roles, newRoles);
    setAssignedRolesFieldValue(i, mergedRoles);
  });
};

/**
 *  Unassign role from all other contacts besides delegate.
 */
export const unassignSelectedRoleFromOtherContacts = (
  thisContactRole: RoleLabel,
  sameAsRole: RoleLabel | undefined,
  contactDetails: ContactDetails[],
  setAssignedRolesFieldValue: (index: number, roles: AssignedRoles) => void,
): void => {
  contactDetails.forEach((delegateContact, i) => {
    if (delegateContact.role === sameAsRole) {
      return;
    }
    if (roleLabelToValueMap[thisContactRole](delegateContact.roles)) {
      const override = buildAssignedRoles[thisContactRole](false);
      const replacedRoles = replaceAssignedRoles(
        delegateContact.roles,
        override,
      );
      setAssignedRolesFieldValue(i, replacedRoles);
    }
  });
};

/**
 * Merges two ContactFieldsConfig.
 * - Optional: both need to be optional for the new field to also be optional.
 * - Show: either need to be enabled for the field to show.
 * - Disabled: either need to be disabled for the field to be disabled.
 */
const and = (a: boolean, b: boolean) => a && b;
const or = (a: boolean, b: boolean) => a || b;
export const mergeContactFieldsConfig = (
  c1: ContactFieldsConfig,
  c2: ContactFieldsConfig,
): ContactFieldsConfig => ({
  optionalFirstName: and(c1.optionalFirstName, c2.optionalFirstName),
  optionalLastName: and(c1.optionalLastName, c2.optionalLastName),
  optionalEmail: and(c1.optionalEmail, c2.optionalEmail),
  optionalMobile: and(c1.optionalMobile, c2.optionalMobile),
  optionalAlternatePhone: and(
    c1.optionalAlternatePhone,
    c2.optionalAlternatePhone,
  ),
  optionalBusinessName: and(c1.optionalBusinessName, c2.optionalBusinessName),
  showEmail: or(c1.showEmail, c2.showEmail),
  showPhone: or(c1.showPhone, c2.showPhone),
  showBusinessName: or(c1.showBusinessName, c2.showBusinessName),
  showBusinessAddress: or(c1.showBusinessAddress, c2.showBusinessAddress),
  disabledFirstName: or(c1.disabledFirstName, c2.disabledFirstName),
  disabledLastName: or(c1.disabledLastName, c2.disabledLastName),
  disabledEmail: or(c1.disabledEmail, c2.disabledEmail),
  disabledMobile: or(c1.disabledMobile, c2.disabledMobile),
  disabledAlternatePhone: or(
    c1.disabledAlternatePhone,
    c2.disabledAlternatePhone,
  ),
  disabledBusinessName: or(c1.disabledBusinessName, c2.disabledBusinessName),
  disableBusinessAddress: or(
    c1.disableBusinessAddress,
    c2.disableBusinessAddress,
  ),
});

/**
 * Merges two ContactPreferencesConfig.
 * Disabled: either need to be disabled for the field to be disabled.
 */
export const mergeContactPreferencesConfig = (
  config1: ContactPreferencesConfig,
  config2: ContactPreferencesConfig,
): ContactPreferencesConfig => {
  return {
    ...config1,
    emailDisabled: config1.emailDisabled || config2.emailDisabled,
    smsDisabled: config1.smsDisabled || config2.smsDisabled,
  };
};

/**
 * Merges two ContactPreferencesConfig arrays.
 */
export const mergeContactPreferencesConfigLists = (
  array1: ContactPreferencesConfig[],
  array2: ContactPreferencesConfig[],
): ContactPreferencesConfig[] => {
  return Array.from(
    [...array1, ...array2]
      .reduce((acc, curr) => {
        const existing = acc.get(curr.contactPreferenceType);
        acc.set(
          curr.contactPreferenceType,
          existing ? mergeContactPreferencesConfig(existing, curr) : curr,
        );
        return acc;
      }, new Map<ContactPreferenceType, ContactPreferencesConfig>())
      .values(),
  );
};

/**
 * Merges two ContactConfig objects.
 */
export const mergeContactConfig = (
  config1: ContactConfig,
  config2: ContactConfig,
) => {
  return {
    ...config1,
    fieldsConfig: mergeContactFieldsConfig(
      config1.fieldsConfig,
      config2.fieldsConfig,
    ),
    preferencesConfig: mergeContactPreferencesConfigLists(
      config1.preferencesConfig,
      config2.preferencesConfig,
    ),
  };
};

/**
 * Combines contactConfig objects based on what roles are selected.
 * I.e. if a contact is a Requester and a Quote payer, then fields applicable to
 * both (i.e. billing address) should be enabled.
 */
export const combineRolesContactConfig = (
  contactConfigs: ContactConfig[],
  contactDetails: ContactDetails[],
): ContactConfig[] => {
  const map: Map<RoleLabel, ContactConfig> = new Map(
    contactConfigs.map((contactConfig) => [contactConfig.role, contactConfig]),
  );

  contactDetails.forEach(({ role, sameAsRole }) => {
    if (!sameAsRole) {
      return;
    }

    const contactConfig = map.get(role);
    const sameAsConfig = map.get(sameAsRole);
    if (sameAsConfig && contactConfig) {
      map.set(sameAsRole, mergeContactConfig(sameAsConfig, contactConfig));
    }
  });

  return Array.from(map.values());
};
